import moment from '@e3dc-react/shell/Libs/moment'
import useDynamicPricesSettings from 'src/Libs/dynamic-power-prices/hooks/use-dynamic-prices-settings'
import usePowerPrices from 'src/Libs/dynamic-power-prices/hooks/use-power-prices'
import { DynamicPricesSettings, PowerPrice } from 'src/Libs/dynamic-power-prices/types'
import { fromDateForModal, toDateForModal, fromDate, toDate, getEarlierDate, getLaterDate } from '../utils/dateUtils'
import { filterOutInvalidTimestamps, filterOutOfRangePrices } from 'src/Libs/dynamic-power-prices/utils/cost-saving-chart'
import { useDynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-provider'

export type DynamicPowerPriceWidgetData = {
  powerPricesForWidget?: PowerPrice[]
  currentPowerPriceValues: PowerPrice
  powerPricesForModal?: PowerPrice[]
  lastFetchedTimestampForModal: string
  dynamicPricesSettings: DynamicPricesSettings | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesSettings) => Promise<void>
  isDynamicPowerPriceWidgetDataLoading: boolean
}

const FIFTEEN_MINUTES_MS = moment.duration(15, 'minutes').asMilliseconds()

/*
 * A hook that fetches the data for the dynamic power price widget.
 */
const useDataForDppWidget = ({ installationId, storageId }: { installationId?: string; storageId?: string }): DynamicPowerPriceWidgetData => {
  // Only fetch power prices if dynamic power prices are enabled for the installation
  const { isDynamicPowerPriceEnabled } = useDynamicPowerPricesEnabledContext()
  // Combine both widget and modal data fetching into one to reduce the number of requests
  const { powerPrices, currentPowerPriceValues, lastFetchedTimestamp, isPowerPricesLoading } = usePowerPrices({
    installationId,
    fromDate: getEarlierDate(fromDate, fromDateForModal),
    toDate: getLaterDate(toDate, toDateForModal),
    refetchInterval: FIFTEEN_MINUTES_MS,
    enabled: isDynamicPowerPriceEnabled,
  })

  // Filter out prices that are outside of the widget/modal date ranges
  const powerPricesForWidget = filterOutOfRangePrices(powerPrices, fromDate, toDate)
  const powerPricesForModal = filterOutOfRangePrices(powerPrices, fromDateForModal, toDateForModal)

  const {
    isLoading: isDynamicPricesSettingsLoading,
    dynamicPricesSettings,
    handleDynamicPricesSettingsUpdate,
  } = useDynamicPricesSettings({ storageId: storageId || '' })

  const isDynamicPowerPriceWidgetDataLoading = isPowerPricesLoading || isDynamicPricesSettingsLoading
  const powerPricesForWidgetInTimeRange = filterOutInvalidTimestamps(powerPricesForWidget ?? [], fromDate, toDate) as PowerPrice[]
  const powerPricesForModalInTimeRange = filterOutInvalidTimestamps(powerPricesForModal ?? [], fromDateForModal, toDateForModal) as PowerPrice[]

  return {
    powerPricesForWidget: powerPricesForWidgetInTimeRange,
    currentPowerPriceValues,
    powerPricesForModal: powerPricesForModalInTimeRange,
    lastFetchedTimestampForModal: lastFetchedTimestamp,
    dynamicPricesSettings,
    handleDynamicPricesSettingsUpdate,
    isDynamicPowerPriceWidgetDataLoading,
  }
}

export default useDataForDppWidget
